import classNames from 'classnames';

import React from 'react';

import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';

import { useTranslation } from 'react-i18next';

import Button from 'reactstrap/lib/Button';
import Form from 'reactstrap/lib/Form';
import FormGroup from 'reactstrap/lib/FormGroup';
import Modal from 'reactstrap/lib/Modal';
import ModalBody from 'reactstrap/lib/ModalBody';
import ModalFooter from 'reactstrap/lib/ModalFooter';
import ModalHeader from 'reactstrap/lib/ModalHeader';
import Spinner from 'reactstrap/lib/Spinner';

import { requestPasswordReset } from '@ttstr/api';
import { EMAIL_PATTERN } from '@ttstr/utils';
import FormField from '../Form/FormField';

interface OwnProps {
  isOpen: boolean;
  toggle(): void;
}

type Props = Readonly<OwnProps>;

interface PasswordReset {
  email: string;
}

const PasswordResetModal: React.FC<Props> = ({ isOpen, toggle }) => {
  const [success, setSuccess] = React.useState(false);
  const { t } = useTranslation();
  const formContext = useForm<PasswordReset>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });
  const { handleSubmit, register, formState } = formContext;
  const { isSubmitting } = formState;

  const onSubmit: SubmitHandler<PasswordReset> = async ({ email }) => {
    await requestPasswordReset(email);
    setSuccess(true);
  };

  const toggleModal = isSubmitting ? null : toggle;

  return (
    <Modal
      size="sm"
      fade
      backdrop="static"
      isOpen={isOpen}
      toggle={toggleModal}
      unmountOnClose
      onClosed={() => setSuccess(false)}
    >
      <ModalHeader toggle={toggleModal}>{t(`PASSWORD_RESET.TITLE`)}</ModalHeader>
      {success ? (
        <>
          <ModalBody>
            <p className="text-success">{t(`PASSWORD_RESET.SUCCESS_MAIL`)}</p>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" block type="button" onClick={toggle}>
              {t(`PASSWORD_RESET.CLOSE`)}
            </Button>
          </ModalFooter>
        </>
      ) : (
        <FormProvider {...formContext}>
          <Form onSubmit={handleSubmit(onSubmit)} noValidate>
            <fieldset disabled={isSubmitting}>
              <ModalBody>
                <FormGroup tag="fieldset">
                  <p>{t('PASSWORD_RESET.DESCRIPTON')}</p>
                  <FormField
                    type="email"
                    id="email"
                    name="email"
                    placeholder={t(`CUSTOMER.EMAIL`)}
                    autoComplete="username"
                    label={t(`CUSTOMER.EMAIL`)}
                    srOnlyLabel
                    innerRef={register({
                      required: true,
                      validate: {
                        'STRING.EMAIL': (value) => EMAIL_PATTERN.test(value),
                      },
                    })}
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter className="flex-row-reverse justify-content-between">
                <Button color="primary" type="submit">
                  {t(`PASSWORD_RESET.PROCEED`)}
                  <span className={classNames('spinner-wrapper', { invisible: !isSubmitting })}>
                    <Spinner color="primary" size="sm" />
                  </span>
                </Button>
                <Button onClick={toggleModal} color="link" type="button">
                  {t(`PASSWORD_RESET.CANCEL`)}
                </Button>
              </ModalFooter>
            </fieldset>
          </Form>
        </FormProvider>
      )}
    </Modal>
  );
};

export default PasswordResetModal;
